import {Trans} from '@lingui/macro';
import {capitalize} from 'lodash';
import React, {ReactNode, useEffect, useState} from 'react';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useUnmountCancelToken} from '../../../../srcCommon/helpers/hookHelpers';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {
  ApiCustomField,
  ApiCustomFields,
  CustomFieldBaseModel,
  CustomFieldErrorCodesEnum,
  CustomFieldModel,
} from '../../../models/api/customFieldModel';
import {SettingsCustomFieldsPagesEnum} from '../../../models/url/settingsUrlModel';
import {fetchApiAsync, patchApiAsync} from '../../../services/http/http';
import {getCompanyUrl} from '../../../store/data/company/companySelectors';
import {getCustomFieldsBaseUrl, parseApiError} from '../customFieldsHelpers';
import {CustomFieldsDeleteDialog} from '../dialog/customFieldsDeleteDialog';
import {CustomFieldForm} from './customFieldForm';
import {CustomFieldFormSkeleton} from './customFieldFormSkeleton';

/*
 * Types.
 */

interface EditCustomFieldFormProps {
  tabPage: SettingsCustomFieldsPagesEnum;
  customFieldId: CustomFieldModel['id'];
  setSuccessBannerMessage: HandlerOf<ReactNode>;
  setErrorBannerMessage: HandlerOf<ReactNode>;
  onDeleteCustomFields: HandlerOf<ReadonlyArray<number>>;
}

/*
 * Component.
 */

export const EditCustomFieldForm = ({
  tabPage,
  customFieldId,
  setSuccessBannerMessage,
  setErrorBannerMessage,
  onDeleteCustomFields,
}: EditCustomFieldFormProps) => {
  const companyUrl = useAppSelector(getCompanyUrl);
  const [customField, setCustomField] = useState<CustomFieldModel | undefined>(undefined);
  const [shouldDisplayDeleteDialog, setShouldDisplayDeleteDialog] = useState(false);
  const [errors, setErrors] = useState({});
  const cancelToken = useUnmountCancelToken();
  const customFieldsApiUrl = `${companyUrl}/${tabPage}/custom_fields`;
  const {navigateToLink} = useLinkNavigation();

  useEffect(() => {
    fetchApiAsync(ApiCustomFields, customFieldsApiUrl, {cancelToken}).then(
      (data) => setCustomField(data.find((f) => f.id === customFieldId)),
      (err) => setErrorBannerMessage(parseApiError(err.body || err)),
    );
  }, [customFieldsApiUrl]);

  const onSubmitClick = async (data: CustomFieldBaseModel) => {
    if (!customField) {
      return;
    }

    setErrors({});
    try {
      await patchApiAsync(ApiCustomField, `${companyUrl}/custom_fields/${customField.id}`, data, {
        cancelToken,
      });
      setSuccessBannerMessage(
        <Trans id="custom-fields-edit-success">
          Edited &quot;{capitalize(data.name)}&quot; custom field
        </Trans>,
      );
      navigateToLink(`${getCustomFieldsBaseUrl(tabPage)}/overview`);
    } catch (err) {
      if (err.body.code === CustomFieldErrorCodesEnum.CUSTOM_FIELD_ALREADY_EXIST) {
        setErrors({name: <Trans id="custom-fields-edit-conflict-error">This name already exists</Trans>});
      } else {
        setErrorBannerMessage(parseApiError(err.body || err));
      }

      throw err;
    }
  };

  if (!customField) {
    return <CustomFieldFormSkeleton />;
  }

  const onDeleteCustomField = () => {
    setShouldDisplayDeleteDialog(false);
    onDeleteCustomFields([customField.id]);
    setSuccessBannerMessage(
      <Trans id="custom-fields-edit-deleted">Deleted &ldquo;{customField.name}&ldquo; custom field</Trans>,
    );
    navigateToLink(`${getCustomFieldsBaseUrl(tabPage)}/overview`);
  };

  return (
    <>
      <CustomFieldForm
        tabPage={tabPage}
        customField={customField}
        onSubmit={onSubmitClick}
        errors={errors}
        setErrors={setErrors}
        renderActions={() => renderEditFormActions(setShouldDisplayDeleteDialog)}
        isEditing
      />
      {shouldDisplayDeleteDialog && (
        <CustomFieldsDeleteDialog
          customFields={[customField]}
          tabPage={tabPage}
          onDelete={onDeleteCustomField}
          onClose={() => setShouldDisplayDeleteDialog(false)}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

function renderEditFormActions(setShouldDisplayDeleteDialog: HandlerOf<boolean>) {
  return (
    <Button variant={ButtonVariantsEnum.SECONDARY_DANGER} onClick={() => setShouldDisplayDeleteDialog(true)}>
      <Trans id="settings-custom-fields-edit-form-delete-action">Delete custom field</Trans>
    </Button>
  );
}
